import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'

/**
 * Shared lifecycle plumbing for our overlay modals (ConfirmModal,
 * PhotoZoomModal, VehicleTagModal) and the mobile nav drawer in Navbar.vue.
 *
 * Four responsibilities, all of them previously duplicated four times:
 *   1. ESC key closes the overlay (only when open).
 *   2. `document.body.style.overflow` is locked to `hidden` while open so the
 *      page underneath can't be wheel-scrolled through the backdrop.
 *   3. Focus management: on open, capture previously-focused element + move
 *      focus into the modal; on close, restore. Tab/Shift+Tab is trapped
 *      inside the modal so keyboard users can't escape into background
 *      content without explicitly closing the dialog (WAI-ARIA dialog
 *      pattern requirement).
 *   4. Listener + body-style cleanup on unmount AND when `open` flips back to
 *      false (covers the "two modals open in series" race).
 *
 * Usage:
 *   const props = defineProps({ open: { type: Boolean, default: false } })
 *   const emit  = defineEmits(['close'])
 *   const rootEl = ref(null)
 *   useModalShell(() => props.open, () => emit('close'), rootEl)
 *
 * The `rootEl` ref points at the modal's outermost element (the one with
 * role="dialog"); focus trap queries tabbable descendants inside it. Pass
 * null/omit if the modal renders no focusable content (e.g. pure
 * info-only) - the trap silently no-ops.
 *
 * @param {() => boolean} isOpen
 * @param {() => void} onClose
 * @param {{ value: HTMLElement | null } | null} rootEl
 */
export function useModalShell(isOpen, onClose, rootEl = null) {
    let previouslyFocused = null

    const tabbableSelector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

    function getTabbable() {
        if (!rootEl?.value) return []
        return Array.from(rootEl.value.querySelectorAll(tabbableSelector)).filter(
            (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
        )
    }

    function handleKey(event) {
        if (!isOpen()) return

        if (event.key === 'Escape') {
            onClose()
            return
        }

        if (event.key !== 'Tab' || !rootEl?.value) return

        const tabbable = getTabbable()
        if (tabbable.length === 0) {
            // Nothing focusable inside - prevent Tab from escaping the modal
            // entirely (keyboard user would otherwise jump to navbar/footer).
            event.preventDefault()
            return
        }

        const first = tabbable[0]
        const last = tabbable[tabbable.length - 1]
        const active = document.activeElement

        if (event.shiftKey && (active === first || !rootEl.value.contains(active))) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && active === last) {
            event.preventDefault()
            first.focus()
        }
    }

    watch(isOpen, async (open) => {
        if (typeof document === 'undefined') return
        document.body.style.overflow = open ? 'hidden' : ''

        if (open) {
            previouslyFocused = document.activeElement
            // Wait one tick for the modal root + tabbable descendants to mount.
            await nextTick()
            const tabbable = getTabbable()
            if (tabbable.length > 0) {
                tabbable[0].focus()
            } else if (rootEl?.value) {
                rootEl.value.focus()
            }
        } else if (previouslyFocused instanceof HTMLElement) {
            // Hand keyboard focus back to the element that opened the modal
            // (usually the trigger button) so a screen-reader user keeps
            // their reading position.
            previouslyFocused.focus()
            previouslyFocused = null
        }
    })

    onMounted(() => window.addEventListener('keydown', handleKey))
    onBeforeUnmount(() => {
        window.removeEventListener('keydown', handleKey)
        if (typeof document !== 'undefined') document.body.style.overflow = ''
    })
}
